import type { Invite, User, UserGroup } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import { Globe } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';

interface UserFormProps {
  user?: User | (Invite & { isInvite: true });
  onSubmit: (data: UserFormValues) => Promise<void>;
  allowedGroups: UserGroup[];
  groupsLoading?: boolean;
}

export type UserFormValues = z.infer<typeof userFormSchema>;

const userFormSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  userGroup: z.string().min(1),
  language: z.enum(['de-DE', 'en-US']),
});

const resolveGroupId = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'id' in value) return String((value as { id?: string }).id);
  return '';
};

export const UserForm = ({ user, onSubmit, allowedGroups, groupsLoading }: UserFormProps) => {
  const { t } = useIntl();
  const [isLoading, setIsLoading] = useState(false);

  const defaultEmail =
    import.meta.env.DEV && !user
      ? import.meta.env.VITE_DEV_EMAIL?.replace(
          '{{val}}',
          Math.random().toString(36).substring(7)
        ) || ''
      : '';
  const defaultFirstName = import.meta.env.DEV && !user ? 'Dev' : '';
  const defaultLastName = import.meta.env.DEV && !user ? 'User' : '';

  const form = useForm<UserFormValues, any, UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: user?.email ?? defaultEmail,
      firstName: user?.firstName ?? defaultFirstName,
      lastName: user?.lastName ?? defaultLastName,
      userGroup: resolveGroupId(user?.userGroup) || allowedGroups[0]?.id || '',
      language: user?.language ?? 'de-DE',
    },
  });

  useEffect(() => {
    return () => {
      form.clearErrors();
    };
  }, [form]);

  useEffect(() => {
    if (allowedGroups.length === 0) return;
    const currentValue = form.getValues('userGroup');
    if (!currentValue || !allowedGroups.some((group) => group.id === currentValue)) {
      form.setValue('userGroup', allowedGroups[0].id, { shouldValidate: true });
    }
  }, [allowedGroups, form]);
  const handleSubmit = async (data: UserFormValues) => {
    setIsLoading(true);
    form.clearErrors();
    try {
      await onSubmit(data);
    } catch (err) {
      if (err && typeof err === 'object' && 'name' in err && err.name === 'PayloadApiError') {
        const payloadError = err as { response?: { errors?: Array<{ message: string }> } };
        if (payloadError.response?.errors?.some((e) => e.message === 'EmailAlreadyUsed')) {
          form.setError('email', { type: 'manual', message: t('users.emailAlreadyUsed') });
        } else if (
          payloadError.response?.errors?.some((e) => e.message === 'LastCustomerAdminRoleChange')
        ) {
          form.setError('userGroup', {
            type: 'manual',
            message: t('users.lastCustomerAdminGroupChange'),
          });
        } else if (
          payloadError.response?.errors?.some((e) => e.message === 'LastGmnAdminRoleChange')
        ) {
          form.setError('userGroup', {
            type: 'manual',
            message: t('users.lastGmnAdminGroupChange'),
          });
        } else if (
          payloadError.response?.errors?.some((e) => e.message === 'CustomerUserQuotaExceeded')
        ) {
          form.setError('root', { type: 'manual', message: t('users.quotaExceeded') });
        } else {
          form.setError('root', { type: 'manual', message: t('errors.unknownError') });
        }
      } else {
        form.setError('root', { type: 'manual', message: t('errors.unknownError') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>{t('auth.email')}</FormLabel>
              <FormControl>
                <Input placeholder={t('auth.email')} {...field} disabled={!!user} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>{t('users.firstName')}</FormLabel>
              <FormControl>
                <Input placeholder={t('users.firstName')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>{t('users.lastName')}</FormLabel>
              <FormControl>
                <Input placeholder={t('users.lastName')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {(!user || ('isInvite' in user && user.isInvite)) && (
          <FormField
            control={form.control}
            name="language"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('settings.language')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <div className="flex items-center gap-3">
                        <Globe className="size-4 " />
                        <SelectValue placeholder={t('settings.selectLanguage')} />
                      </div>
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="de-DE">{t('settings.german')}</SelectItem>
                    <SelectItem value="en-US">{t('settings.english')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="userGroup"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('users.group')}</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={groupsLoading}
              >
                <FormControl>
                  <SelectTrigger data-testid="user-group-select">
                    <SelectValue placeholder={t('users.selectGroup')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {allowedGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {t(`portalRoles.${group.key}` as unknown as never)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.formState.errors.root && (
          <FormMessage>{form.formState.errors.root.message}</FormMessage>
        )}
        <Button type="submit" className="mt-6" disabled={isLoading}>
          {isLoading
            ? user
              ? t('common.saving')
              : t('common.sending')
            : user
              ? t('common.save')
              : t('auth.sendInvitation')}
        </Button>
      </form>
    </Form>
  );
};
